import { Slot } from '@radix-ui/react-slot';
import { type AnchorHTMLAttributes, type HTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

/**
 * Card primitive. Opaque `surface` background, 1px `border`, `radius-lg`
 * corners, `shadow-sm` at rest. Hover lifts to `surface-2` + `border-strong`.
 * The hover effect is opt-in via `interactive` — purely-decorative cards
 * shouldn't react to hover.
 *
 * Composition mirrors shadcn — `<Card>` is the shell; `<CardHeader>`,
 * `<CardTitle>`, `<CardDescription>`, `<CardBody>`, `<CardFooter>` are
 * conventional slots.
 *
 * **Keyboard focus (WCAG 2.4.7):** `<Card interactive>` has no focus indicator
 * of its own. Wrap navigable cards in `<CardLink>` so keyboard users see a
 * visible ring on the focusable element:
 *
 *   <CardLink asChild>
 *     <Link to="/products/p1"><Card interactive>…</Card></Link>
 *   </CardLink>
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-border bg-surface text-fg shadow-sm transition-colors duration-200 ease-out',
        interactive && 'hover:border-border-strong hover:bg-surface-2',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 px-5 pb-3 pt-5', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-md font-medium leading-tight tracking-tight text-fg', className)}
      {...props}
    >
      {children}
    </h3>
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-fg-3', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-5 pb-5', className)} {...props} />
  ),
);
CardBody.displayName = 'CardBody';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-3 border-t border-border-subtle bg-surface-2/40 px-5 py-3',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export interface CardLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  asChild?: boolean;
}

/**
 * Focus-safe wrapper for navigable `<Card>` tiles (WCAG 2.4.7 Focus Visible).
 *
 * `<Card interactive>` has hover styles but no keyboard focus indicator.
 * `<CardLink>` is the focusable element that carries the design-system ring,
 * so Tab-key navigation shows the outline on the correct DOM node.
 *
 *   <CardLink asChild>
 *     <Link to="/products/p1"><Card interactive>…</Card></Link>
 *   </CardLink>
 *
 * `asChild` forwards props + ref to the child via Radix Slot — use it to
 * swap the underlying `<a>` for any router link (`<Link>`, `<NavLink>`).
 */
export const CardLink = forwardRef<HTMLAnchorElement, CardLinkProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return (
      <Comp
        ref={ref}
        className={cn(
          'block rounded-lg focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          className,
        )}
        {...props}
      />
    );
  },
);
CardLink.displayName = 'CardLink';
